import { clientWorkspaceStorage } from '../common/organizationAccessMode';
import React, { useState, useEffect, useRef } from 'react';
import { ICONS } from './constants';
import { generateGenericPdf } from '../utils/pdfGenerator';

const Notepad: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [content, setContent] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Load from local storage on mount
    useEffect(() => {
        const savedNote = clientWorkspaceStorage().getItem('inteloptics_global_notes');
        if (savedNote) {
            setContent(savedNote);
        }
    }, []);

    // Save to local storage on change
    useEffect(() => {
        const timer = setTimeout(() => {
            clientWorkspaceStorage().setItem('inteloptics_global_notes', content);
        }, 500); // Debounce save
        return () => clearTimeout(timer);
    }, [content]);

    const handleDownload = () => {
        if (!content.trim()) return;
        generateGenericPdf(
            { observations: content }, 
            "Intelligence Notepad"
        );
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-24 left-4 md:left-8 w-14 h-14 bg-slate-800 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-700 transition-all transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-slate-300 z-40 group"
                aria-label="Open Notepad"
                title="Global Intelligence Notepad"
            >
                {React.cloneElement(ICONS.notepad as any, { className: 'h-6 w-6 group-hover:animate-pulse' })}
            </button>
        );
    }

    return (
        <div 
            className={`fixed left-4 md:left-8 z-40 bg-white rounded-xl shadow-2xl border border-slate-300 flex flex-col transition-all duration-300 ease-in-out overflow-hidden
                ${isMinimized ? 'bottom-24 w-64 h-12' : 'bottom-24 w-[calc(100%-2rem)] max-w-md h-[60vh] max-h-[600px]'}
            `}
        >
            {/* Header */}
            <div 
                className="flex items-center justify-between p-3 bg-slate-100 border-b border-slate-200 cursor-pointer select-none"
                onClick={() => setIsMinimized(!isMinimized)}
            >
                <div className="flex items-center gap-2">
                    <div className="p-1 bg-slate-200 rounded text-slate-600">
                        {React.cloneElement(ICONS.notepad as any, { className: 'h-4 w-4' })}
                    </div>
                    <h3 className="font-bold text-slate-700 text-sm">Intel Notepad</h3>
                </div>
                <div className="flex items-center gap-1">
                    <button 
                        onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                        className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Download Notes"
                    >
                        {React.cloneElement(ICONS.download as any, { className: 'h-4 w-4' })}
                    </button>
                    <button 
                        onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
                        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            {isMinimized ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                            )}
                        </svg>
                    </button>
                    <button 
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Content */}
            {!isMinimized && (
                <div className="flex-1 flex flex-col bg-white">
                    <textarea
                        ref={textareaRef}
                        className="flex-1 w-full p-4 resize-none focus:outline-none text-sm text-slate-700 font-mono leading-relaxed"
                        placeholder="Record intelligence observations here..."
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        spellCheck={false}
                    />
                    <div className="p-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400 flex justify-between">
                        <span>{content.length} chars</span>
                        <span>Auto-saving to local storage</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Notepad;